import { useContext, useEffect, useState } from "react";
import dayjs from "dayjs";
import type { Destination, SelectOption } from "../../../../types";
import {
  API_DATE_FORMAT,
  DATE_FORMAT,
  SEARCH_PARAMS,
  getTravelerOptions,
} from "../../../utils/constants/constants";
import {
  getLastSearchRequestFromLocalStorage,
  getPartnerCode,
} from "@/app/utils/services/service/localStorageService";
import { useRouter } from "@/i18n/navigation";
import { useDatePickerContext } from "../DatePicker/DatePickerContext";
import { redirect } from "@/app/utils/services/helper/helper";
import { CurrencyContext } from "@/app/context/Search/CurrencyContext";
import type { ReadonlyURLSearchParams } from "next/navigation";
import { useLocale } from "next-intl";
import { useTranslations } from "use-intl";

type Props = {
  searchParams: ReadonlyURLSearchParams;
  router: ReturnType<typeof useRouter>;
  isHomePage: boolean;
  destination: {};
  setDestination: (val: Destination) => void;
};

type RequestSearchParams = {
  [key: string]: string | null;
};

const useSetSearchParams = (props: Props) => {
  const locale = useLocale();
  const t = useTranslations();
  const travellerOptionsBase = getTravelerOptions(t);
  const { searchParams, isHomePage, setDestination } = props;
  const { setUpdateCurrency } = useContext(CurrencyContext);
  const [travelersTotal, setTravelersTotal] = useState(2);
  const [travellerOptions, setTravallerOptions] =
    useState<SelectOption[]>(travellerOptionsBase);
  const { setSelectedDate } = useDatePickerContext();

  const lastSearchRequest = getLastSearchRequestFromLocalStorage(locale);

  const getQuerySearchParams = (): RequestSearchParams => {
    return {
      id: searchParams.get(SEARCH_PARAMS.ID),
      destination: searchParams.get(SEARCH_PARAMS.DESTINATION),
      country: searchParams.get(SEARCH_PARAMS.COUNTRY),
      city: searchParams.get(SEARCH_PARAMS.CITY),
      from: searchParams.get(SEARCH_PARAMS.CHECKIN),
      to: searchParams.get(SEARCH_PARAMS.CHECKOUT),
      currency: searchParams.get(SEARCH_PARAMS.CURRENCY),
      numberOfAdults: searchParams.get(SEARCH_PARAMS.ADULTS),
      numberOfInfants: searchParams.get(SEARCH_PARAMS.INFANTS),
      numberOfChildren: searchParams.get(SEARCH_PARAMS.CHILDREN),
    };
  };

  const setQueryParams = () => {
    if (isHomePage) {
      let searchQueryParams: any = {
        adults: 2,
        children: 0,
        infants: 0,
        currency: "USD",
      };

      const params: RequestSearchParams = getQuerySearchParams();

      if (lastSearchRequest) {
        const {
          city,
          country,
          destination,
          from,
          to,
          numberOfAdults,
          numberOfChildren,
          numberOfInfants,
          currency,
          id,
        } = lastSearchRequest;

        searchQueryParams = {
          city,
          country,
          destination,
          checkIn: from,
          checkOut: to,
          adults: numberOfAdults,
          children: numberOfChildren,
          infants: numberOfInfants,
          currency,
          id,
        };
      }

      const partnerCode = getPartnerCode();

      if (params.destination && params.country && params.city) {
        searchQueryParams.destination = params.destination;
        searchQueryParams.country = params.country;
        searchQueryParams.city = params.city;
        searchQueryParams.id = params.id;
      }

      if (params.from && params.to) {
        searchQueryParams.from = params.from;
        searchQueryParams.to = params.to;
      }

      if (
        params.numberOfAdults &&
        params.numberOfInfants &&
        params.numberOfChildren
      ) {
        searchQueryParams.adults = params.numberOfAdults;
        searchQueryParams.children = params.numberOfInfants;
        searchQueryParams.infants = params.numberOfChildren;
      }

      if (partnerCode) {
        searchQueryParams["partner"] = partnerCode;
      }

      const queryParams = new URLSearchParams(searchQueryParams).toString();

      const sessionId = window.location.href.split("#")[1];
      if (sessionId) {
        const url = new URLSearchParams(`/?${queryParams + "#" + sessionId}`);
        redirect(url);
      } else {
        const url = new URLSearchParams(`/?${queryParams}`);
        redirect(url);
      }
    }
  };

  const getSearchParameters = () => {
    const params: RequestSearchParams = getQuerySearchParams();

    initializeSearchParams(params);
  };

  const initializeSearchParams = ({
    id,
    destination,
    country,
    city,
    to,
    from,
    currency,
    numberOfAdults,
    numberOfInfants,
    numberOfChildren,
  }: RequestSearchParams) => {
    if (destination) {
      setDestination({
        destination,
        country,
        city,
        label: destination,
        id: id || "",
        value: "0",
      });
    }

    const adults = numberOfAdults ?? lastSearchRequest?.numberOfAdults ?? "2";
    const children =
      numberOfChildren ?? lastSearchRequest?.numberOfChildren ?? "0";
    const infants =
      numberOfInfants ?? lastSearchRequest?.numberOfInfants ?? "0";
    setTravelersTotal(Number(adults) + Number(children) + Number(infants));
    travellerOptionsBase[0].value = adults.toString();
    travellerOptionsBase[1].value = children.toString();
    travellerOptionsBase[2].value = infants.toString();
    setTravallerOptions([...travellerOptionsBase]);
  };

  useEffect(() => {
    setQueryParams();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getSearchParameters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]);

  return {
    travelersTotal,
    setTravelersTotal,
    travellerOptions,
    setTravallerOptions,
  };
};

export default useSetSearchParams;
